import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { HiOutlineCash } from 'react-icons/hi';
import { HiCheckCircle } from 'react-icons/hi2';
import { RiCoinLine } from 'react-icons/ri';
import { TbWallet } from 'react-icons/tb';
import { useWallet } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { getErc20Balance, sendUsdt } from '../services/wallet';
import NumberInput, { parseNumberInput } from '../components/NumberInput';
import Modal from '../components/Modal';
import { PlansSkeleton, Skeleton, SkeletonTable } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

function parsePresets(raw, min, max) {
  const list = String(raw || '')
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n) && n > 0);

  const unique = [...new Set(list.map((n) => Number(n)))];
  return unique.filter((n) => {
    if (Number.isFinite(min) && n < min) return false;
    if (Number.isFinite(max) && n > max) return false;
    return true;
  });
}

function money(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0.00';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fillAmount(value, decimals = 6) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) return '';
  return amount.toFixed(decimals).replace(/\.?0+$/, '') || '0';
}

export default function PlansPage() {
  const { config, user, address, requireAuth, connectOpening, refreshUser, loading } = useWallet();
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [walletUsdt, setWalletUsdt] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [flow, setFlow] = useState('');
  const [flowError, setFlowError] = useState('');
  const [flowAmount, setFlowAmount] = useState(null);
  const notified = useRef('');

  const min = Number(config?.plan_min_usdt);
  const max = Number(config?.plan_max_usdt);
  const dailyMax = Number(config?.plan_daily_max_usdt);
  const userMax = Number(config?.plan_user_max_usdt);

  const presets = useMemo(
    () => parsePresets(config?.plan_preset_amounts, min, max),
    [config?.plan_preset_amounts, min, max]
  );
  const wallet = user?.wallet_address || address;
  const walletFill = fillAmount(walletUsdt);

  const loadWalletUsdt = useCallback(async () => {
    if (!wallet || !config?.usdt_contract) {
      setWalletUsdt(null);
      return;
    }
    setWalletBusy(true);
    try {
      const data = await getErc20Balance(config.usdt_contract, wallet);
      setWalletUsdt(Number(data.balance));
    } catch {
      setWalletUsdt(null);
    } finally {
      setWalletBusy(false);
    }
  }, [wallet, config?.usdt_contract]);

  useEffect(() => {
    loadWalletUsdt();
  }, [loadWalletUsdt]);

  useEffect(() => {
    if (!user) {
      setRecords([]);
      setCheckout(null);
      return undefined;
    }
    let active = true;
    setRecordsLoading(true);
    api.myPurchases()
      .then((data) => {
        if (active) setRecords(data.purchases || []);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setRecordsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user]);

  const pendingId = checkout?.status === 'pending' ? checkout.id : null;

  useEffect(() => {
    if (!pendingId) return undefined;
    const timer = setInterval(() => {
      api.purchaseStatus(pendingId)
        .then((data) => {
          const next = data.purchase;
          if (!next) return;
          setRecords((current) => {
            const rest = current.filter((row) => row.id !== next.id);
            return [next, ...rest].sort((a, b) => Number(b.id) - Number(a.id));
          });
          if (next.status === 'pending') {
            setCheckout(next);
            return;
          }
          setCheckout(null);
          if (next.status === notified.current) return;
          notified.current = next.status;
          if (next.status === 'confirmed') {
            refreshUser();
          } else if (next.status === 'rejected') {
            toast.error(next.gateway_note || 'The deposit was not completed');
          }
        })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [pendingId, refreshUser, toast]);

  if (loading || !config) {
    return <PlansSkeleton />;
  }

  async function buy() {
    const value = parseNumberInput(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a USDT amount');
      return;
    }
    if (value < min || value > max) {
      toast.error(`Amount must be between ${min} and ${max} USDT`);
      return;
    }
    if (Number.isFinite(dailyMax) && dailyMax > 0) {
      const usedToday = records.reduce((sum, row) => {
        if (row.status === 'pending') return sum + Number(row.amount_usdt || 0);
        if (row.status !== 'confirmed' || !row.confirmed_at) return sum;
        const day = new Date(row.confirmed_at).toISOString().slice(0, 10);
        const today = new Date().toISOString().slice(0, 10);
        return day === today ? sum + Number(row.amount_usdt || 0) : sum;
      }, 0);
      const remaining = Number((dailyMax - usedToday).toFixed(8));
      if (value > remaining + 1e-8) {
        toast.error(
          remaining > 0
            ? `Daily deposit limit is ${dailyMax} USDT. You can still deposit ${remaining} USDT today.`
            : `Daily deposit limit of ${dailyMax} USDT is already used for today.`
        );
        return;
      }
    }
    if (Number.isFinite(userMax) && userMax > 0) {
      const pending = records.reduce(
        (sum, row) => (row.status === 'pending' ? sum + Number(row.amount_usdt || 0) : sum),
        0
      );
      const usedLifetime = Number(user?.total_invested || 0) + pending;
      const remaining = Number((userMax - usedLifetime).toFixed(8));
      if (value > remaining + 1e-8) {
        toast.error(
          remaining > 0
            ? `Max invest is ${userMax} USDT. You can still deposit ${remaining} USDT.`
            : `Max invest of ${userMax} USDT is already used.`
        );
        return;
      }
    }

    setBusy(true);
    setFlow('fetching');
    setFlowError('');
    setFlowAmount(value);
    try {
      const data = await api.startPurchase(value);
      const purchase = data.purchase;
      if (!purchase?.gateway_address) {
        throw new Error('Deposit address was not returned');
      }
      notified.current = '';
      setCheckout(purchase);
      setRecords((current) => {
        const rest = current.filter((row) => row.id !== purchase.id);
        return [purchase, ...rest].sort((a, b) => Number(b.id) - Number(a.id));
      });
      setFlow('approve');
      await sendUsdt({
        usdtContract: config.usdt_contract,
        merchantWallet: purchase.gateway_address,
        amount: Number(purchase.amount_usdt),
      });
      setAmount('');
      setFlow('success');
      loadWalletUsdt();
      toast.success('Deposit sent successfully.');
    } catch (error) {
      if (error.data?.purchase) setCheckout(error.data.purchase);
      const cancelled = error.code === 4001;
      const message = cancelled
        ? 'Payment cancelled. No USDT was sent.'
        : (error.message || 'The payment could not be completed.');
      setFlowError(message);
      setFlow(cancelled ? 'cancelled' : 'error');
      if (cancelled) toast.info(message);
      else toast.error(message);
    } finally {
      setBusy(false);
    }
  }

  function closeFlow() {
    if (flow === 'fetching' || flow === 'approve') return;
    setFlow('');
    setFlowError('');
  }

  const flowDone = flow === 'success' || flow === 'error' || flow === 'cancelled';

  const selected = parseNumberInput(amount);
  const invested = Number(user?.total_invested || 0);
  const opening = flow === 'fetching' || flow === 'approve';

  return (
    <>
      <PageHeader title="Plans" subtitle="Deposit USDT and activate earning" />
      <div className="panel-grid">
        <motion.section
          className="panel panel--glow panel--tint-cyan"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="eyebrow">Deposit · Earn · Claim</div>
          <h2>Buy a Dynamic Future Defi plan</h2>
          <p className="muted" style={{ maxWidth: '36rem', lineHeight: 1.55 }}>
            Enter an amount and approve the USDT transfer in your wallet. Your plan is credited when the deposit finishes.
          </p>

          <div className="plans-meta-grid" style={{ marginTop: '1rem' }}>
            <div className="stat plans-meta-range dash-tile dash-tile--cyan">
              <span>
                <HiOutlineCash style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Deposit range
              </span>
              <strong>
                {config?.plan_min_usdt ?? <Skeleton width="3rem" height="1.2rem" />}
                <em>– {config?.plan_max_usdt || '—'} USDT</em>
              </strong>
            </div>
            <div className="stat plans-meta-rate dash-tile dash-tile--gold">
              <span>
                <RiCoinLine style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Daily rate
              </span>
              <strong>
                {config?.daily_return_percent || '—'}
                <em>%</em>
              </strong>
            </div>
            <div className="stat plans-meta-daily dash-tile dash-tile--violet">
              <span>
                <HiOutlineCash style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Daily deposit limit
              </span>
              <strong>
                {Number.isFinite(dailyMax) ? dailyMax : '—'}
                <em>USDT / day</em>
              </strong>
            </div>
            <div className="stat plans-meta-user dash-tile dash-tile--green">
              <span>
                <HiOutlineCash style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Max invest
              </span>
              <strong>
                {Number.isFinite(userMax) ? userMax : '—'}
                <em>USDT / user</em>
              </strong>
            </div>
          </div>
        </motion.section>

        <motion.section
          className="panel panel--tint-gold"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3>Purchase</h3>
          <div className="field">
            <div className="field__top">
              <label htmlFor="amount">USDT amount</label>
              <p className="field__available">
                {user
                  ? walletBusy && walletUsdt == null
                    ? 'Reading wallet…'
                    : (
                      <>
                        Available <strong>{walletUsdt == null ? '—' : money(walletUsdt)}</strong> USDT
                      </>
                    )
                  : 'Connect wallet to see USDT'}
              </p>
            </div>
            <NumberInput
              id="amount"
              value={amount}
              onChange={setAmount}
              allowDecimal
              maxDecimals={6}
              placeholder="Enter amount"
            />
            <div className="amount-presets" role="group" aria-label="Preset amounts">
              {presets.map((preset) => {
                const active = Number.isFinite(selected) && selected === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    className={`amount-preset${active ? ' is-active' : ''}`}
                    onClick={() => setAmount(String(preset))}
                    disabled={busy}
                  >
                    {preset}
                  </button>
                );
              })}
              <button
                type="button"
                className={`amount-preset amount-preset--max${walletFill && amount === walletFill ? ' is-active' : ''}`}
                onClick={() => walletFill && setAmount(walletFill)}
                disabled={busy || !user || !walletFill || Number(walletFill) <= 0}
              >
                Max
              </button>
            </div>
          </div>
          <motion.button
            className={`btn btn--primary${connectOpening && !user ? ' is-busy' : ''}`}
            type="button"
            disabled={busy || opening || (connectOpening && !user)}
            aria-busy={opening || (connectOpening && !user)}
            onClick={() => requireAuth(buy, 'Connect your wallet to buy a plan')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {connectOpening && !user ? <span className="btn__spin" aria-hidden /> : <TbWallet />}
            {flow === 'fetching' ? 'Getting address…' : flow === 'approve' ? 'Approve in wallet…' : connectOpening && !user ? 'Connecting…' : user ? 'Buy with USDT' : 'Connect & Buy'}
          </motion.button>
        </motion.section>
      </div>

      <motion.section
        className="panel"
        style={{ marginTop: '1rem' }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
      >
        <div className="stat dash-tile dash-tile--gold" style={{ maxWidth: '320px' }}>
          <span>
            <HiOutlineCash style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Invested
          </span>
          <strong>
            {money(invested)}
            <em>USDT</em>
          </strong>
        </div>
        <h3 style={{ marginTop: '1.25rem' }}>Purchase history</h3>
        {recordsLoading ? (
          <SkeletonTable rows={4} cols={4} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Tx</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td>{money(row.amount_usdt)} USDT</td>
                    <td>
                      <span className={`badge${row.status === 'confirmed' ? '' : ' badge--pending'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      {row.tx_hash && String(row.tx_hash).startsWith('0x') ? (
                        <a href={`https://bscscan.com/tx/${row.tx_hash}`} target="_blank" rel="noreferrer">
                          {row.tx_hash.slice(0, 10)}…
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {!records.length ? (
                  <tr>
                    <td colSpan={4} className="muted">
                      {user ? 'No plan purchases yet.' : 'Connect your wallet to see your investments.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>

      <Modal
        open={Boolean(flow)}
        onClose={flowDone ? closeFlow : undefined}
        className={flow === 'success' ? 'modal--success' : undefined}
        icon={
          flow === 'success' ? (
            <div className="modal-success__mark" aria-hidden>
              <span className="modal-success__ring" />
              <HiCheckCircle />
            </div>
          ) : null
        }
        eyebrow={flow === 'success' ? 'Plan Purchase Successful' : 'Plan deposit'}
        title={
          flow === 'success'
            ? 'Congratulations'
            : flow === 'cancelled'
              ? 'Payment cancelled'
              : flow === 'error'
                ? 'Payment was not sent'
                : flow === 'approve'
                  ? 'Approve the transfer'
                  : 'Preparing deposit'
        }
        subtitle={
          flow === 'fetching'
            ? 'Getting a deposit address for this plan.'
            : flow === 'approve'
              ? `Confirm ${money(flowAmount)} USDT in your wallet.`
              : flow === 'success'
                ? 'The transfer was approved. Your plan activates when the deposit is recorded.'
                : flow === 'cancelled'
                  ? 'No USDT was sent. You can try again whenever you are ready.'
                  : undefined
        }
        footer={
          flowDone ? (
            <>
              {flow === 'success' && flowAmount != null ? (
                <p className="modal-success__amount">
                  {money(flowAmount)}
                  <span>USDT</span>
                </p>
              ) : null}
              <button type="button" className="btn btn--primary btn--block" onClick={closeFlow}>
                {flow === 'success' ? 'Done' : 'Close'}
              </button>
            </>
          ) : null
        }
      >
        {flow === 'fetching' || flow === 'approve' ? (
          <div className="modal__connecting">
            <div className="modal__spinner" />
            <p className="modal__busy">
              {flow === 'fetching' ? 'Requesting the deposit address…' : 'Waiting for wallet approval…'}
            </p>
          </div>
        ) : null}
        {flow === 'error' ? <p className="modal__error">{flowError}</p> : null}
      </Modal>
    </>
  );
}
